import { merchant_account } from '@trashlab/api/src/domains/billing/schemas/merchant-account';
import {
  foreignKey,
  index,
  pgEnum,
  pgTable,
  uuid,
  varchar,
  boolean,
  jsonb,
} from 'drizzle-orm/pg-core';
import { sql } from 'drizzle-orm';
import { customer } from '@trashlab/api/src/domains/customer/schemas/customer';
import {
  PgAchDebit,
  PgBillingDetails,
  PgCard,
} from '@trashlab/api/src/domains/core/types';
import {
  defaultFields,
  defaultHistoryFields,
} from '@trashlab/api/drizzle/default';

export const payment_method_origin = pgEnum('payment_method_origin', [
  'HAULER_INITIATED',
  'ONLINE_ORDER',
  'PAYMENT_LINK',
  'QUOTE',
  'CUSTOMER_PORTAL',
  'CARD_PRESENT',
  'TILLED_IMPORT',
  'LEGACY_UNKNOWN',
]);

export const tilled_payment_method_type = pgEnum('tilled_payment_method_type', [
  'card',
  'ach_debit',
  'eft_debit',
]);

export const payment_method = pgTable(
  'payment_method',
  {
    ...defaultFields.fieldsWithTenantId(),
    merchantAccountId: uuid('merchantAccountId').references(() => merchant_account.id),
    lastModifiedBy: uuid('lastModifiedBy'),
    createdBy: uuid('createdBy'),
    customerId: uuid('customerId'),
    tilledPaymentMethodId: varchar('tilledPaymentMethodId').notNull(),
    tilledCustomerId: varchar('tilledCustomerId').notNull(),
    type: tilled_payment_method_type('type').default('card').notNull(),
    card: jsonb('card').$type<PgCard>(),
    origin: payment_method_origin('origin').notNull(),
    achDebit: jsonb('achDebit').$type<PgAchDebit>(),
    billingDetails: jsonb('billingDetails').$type<PgBillingDetails>(),
    legacySystemId: varchar('legacySystemId', { length: 255 }),
    defaultMethod: boolean('defaultMethod').default(false).notNull(),
    nickname: varchar('nickname', { length: 255 }),
    checkoutCartId: uuid('checkoutCartId'),
    // Single-use card: a trigger on payment_intent archives it after the
    // first successful charge.
    archiveAfterFirstUse: boolean('archiveAfterFirstUse')
      .default(false)
      .notNull(),
  },
  (table) => {
    return {
      merchantAccountId_idx: index('payment_method_merchantAccountId_idx').on(table.merchantAccountId),
      createdAtTs_idx: index('payment_method_createdAtTs_idx').on(
        table.createdAtTs,
      ),
      tenantId_idx: index('payment_method_tenantId_idx').on(table.tenantId),
      tenant_origin_id_idx: index('payment_method_tenant_origin_id_idx').on(
        table.tenantId,
        table.origin,
        table.id,
      ),
      checkoutCartId_idx: index('payment_method_checkoutCartId_idx').on(
        table.checkoutCartId,
      ),
      tilled_payment_method_id_dx: index(
        'payment_method_tilled_payment_method_id_dx',
      ).on(table.tilledPaymentMethodId),
      updatedAtTs_idx: index('payment_method_updatedAtTs_idx').on(
        table.updatedAtTs,
      ),
      updatedAtTs_idx1: index('payment_method_updatedAtTs_idx1').on(
        table.updatedAtTs.desc(),
      ),
      versionNumber_idx: index('payment_method_versionNumber_idx').on(
        table.versionNumber,
      ),
      // Primary customerId index for customer payment method lookups
      customerId_idx: index('payment_method_customerId_idx').on(
        table.customerId,
      ),
      customer_merge_page_idx: index(
        'payment_method_customer_merge_page_idx',
      ).on(table.tenantId, table.customerId, table.id),
      // Optimization: Speed up SubPlan lookups for active payment methods per customer
      // Partial index for active payment methods by tenant and customer
      tenant_customer_active_idx: index(
        'payment_method_tenant_customer_active_idx',
      )
        .on(table.tenantId, table.customerId)
        .where(sql`"archivedAtTs" IS NULL`),

      tilled_card_details_backfill_idx: index(
        'payment_method_tilled_card_details_backfill_idx',
      ).on(table.id).where(sql`${table.type} = 'card'
          AND ${table.tilledPaymentMethodId} IS NOT NULL
          AND (
            ${table.card} IS NULL
            OR jsonb_typeof(${table.card}) != 'object'
            OR NOT (${table.card} ? 'brand')
            OR NOT (${table.card} ? 'last4')
            OR NOT (${table.card} ? 'exp_month')
            OR NOT (${table.card} ? 'exp_year')
            OR NOT (${table.card} ? 'holder_name')
            OR NOT (${table.card} ? 'checks')
            OR NOT (${table.card} ? 'funding')
            OR (
              ${table.card} ? 'checks'
              AND jsonb_typeof(${table.card} -> 'checks') != 'null'
              AND (
                jsonb_typeof(${table.card} -> 'checks') != 'object'
                OR NOT ((${table.card} -> 'checks') ? 'cvc_check')
                OR NOT ((${table.card} -> 'checks') ? 'address_line1_check')
                OR NOT ((${table.card} -> 'checks') ? 'address_postal_code_check')
              )
            )
          )`),

      tenant_tilled_card_details_backfill_idx: index(
        'payment_method_tenant_tilled_card_details_backfill_idx',
      ).on(table.tenantId, table.id).where(sql`${table.type} = 'card'
          AND ${table.tilledPaymentMethodId} IS NOT NULL
          AND (
            ${table.card} IS NULL
            OR jsonb_typeof(${table.card}) != 'object'
            OR NOT (${table.card} ? 'brand')
            OR NOT (${table.card} ? 'last4')
            OR NOT (${table.card} ? 'exp_month')
            OR NOT (${table.card} ? 'exp_year')
            OR NOT (${table.card} ? 'holder_name')
            OR NOT (${table.card} ? 'checks')
            OR NOT (${table.card} ? 'funding')
            OR (
              ${table.card} ? 'checks'
              AND jsonb_typeof(${table.card} -> 'checks') != 'null'
              AND (
                jsonb_typeof(${table.card} -> 'checks') != 'object'
                OR NOT ((${table.card} -> 'checks') ? 'cvc_check')
                OR NOT ((${table.card} -> 'checks') ? 'address_line1_check')
                OR NOT ((${table.card} -> 'checks') ? 'address_postal_code_check')
              )
            )
          )`),

      // Index for nickname search (case-insensitive pattern matching)
      nickname_search_idx: index('payment_method_nickname_search_idx').on(
        sql`LOWER(${table.nickname})`,
      ),

      // GIN indexes for JSONB fields to enable fast searching
      card_gin_idx: index('payment_method_card_gin_idx').using(
        'gin',
        sql`${table.card} jsonb_path_ops`,
      ),
      ach_debit_gin_idx: index('payment_method_ach_debit_gin_idx').using(
        'gin',
        sql`${table.achDebit} jsonb_path_ops`,
      ),
      billing_details_gin_idx: index(
        'payment_method_billing_details_gin_idx',
      ).using('gin', sql`${table.billingDetails} jsonb_path_ops`),

      //foreign keys
      payment_method_customerId_fkey: foreignKey({
        columns: [table.customerId],
        foreignColumns: [customer.id],
        name: 'payment_method_customerId_fkey',
      }),
    };
  },
);

export const payment_method_history = pgTable('payment_method_history', {
  ...defaultHistoryFields.historyFieldsWithTenantId(),
  merchantAccountId: uuid('merchantAccountId'),
  lastModifiedBy: uuid('lastModifiedBy'),
  createdBy: uuid('createdBy'),
  customerId: uuid('customerId'),
  tilledPaymentMethodId: varchar('tilledPaymentMethodId'),
  tilledCustomerId: varchar('tilledCustomerId'),
  type: tilled_payment_method_type('type'),
  card: jsonb('card'),
  origin: payment_method_origin('origin'),
  achDebit: jsonb('achDebit'),
  billingDetails: jsonb('billingDetails'),
  legacySystemId: varchar('legacySystemId', { length: 255 }),
  defaultMethod: boolean('defaultMethod').default(false).notNull(),
  nickname: varchar('nickname', { length: 255 }),
  checkoutCartId: uuid('checkoutCartId'),
  archiveAfterFirstUse: boolean('archiveAfterFirstUse'),
});

export type PaymentMethodSelect = typeof payment_method.$inferSelect;
